import { useState, useEffect } from "react";
import {
  InsightsCard,
  InsightsDividerList,
  InsightsDividerRow,
  MaskedAccountNumber,
  formatDate,
  formatCurrency,
} from "./InsightsCard";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { decryptPersonData } from "@/shared/utils/serverEncryption";
import { useCitation, getCitationForField } from "./CitationContext";

interface Beneficiary {
  name?: string;
  relationship?: string;
  percentage?: number;
}

export interface InsurancePolicyExtraction {
  insurance_company_name?: string;
  policy_number?: string;
  policy_type?: string;
  face_value?: number;
  cash_value?: number;
  policy_effective_date?: string;
  policy_expiration_date?: string;
  premium_amount?: number;
  premium_frequency?: string;
  insured_person?: string;
  policy_owner?: string;
  primary_beneficiaries?: Beneficiary[];
  contingent_beneficiaries?: Beneficiary[];
}

export default function InsuranceDocumentInsights({
  data,
  citations,
}: {
  data: InsurancePolicyExtraction;
  citations?: Record<string, unknown> | null;
}) {
  const { showCitation } = useCitation();

  const handleFieldClick = (fieldPath: string) => {
    const citation = getCitationForField(citations, fieldPath);
    if (citation) showCitation(citation);
  };

  const {
    insurance_company_name,
    policy_number,
    policy_type,
    face_value,
    cash_value,
    policy_effective_date,
    policy_expiration_date,
    premium_amount,
    premium_frequency,
    insured_person,
    policy_owner,
    primary_beneficiaries,
    contingent_beneficiaries,
  } = data;

  const { person } = useAppContext();

  const [decryptedPolicyNumber, setDecryptedPolicyNumber] = useState<string | null>(null);
  const [isLoadingDecryption, setIsLoadingDecryption] = useState(false);

  useEffect(() => {
    if (!policy_number || !person?.estate_id) {
      return;
    }

    const decryptPolicyNumber = async () => {
      setIsLoadingDecryption(true);
      try {
        const decrypted = await decryptPersonData(person.estate_id, policy_number, "policy_number");
        setDecryptedPolicyNumber(decrypted);
      } catch (error) {
        console.error("Failed to decrypt policy number:", error);
        setDecryptedPolicyNumber(null);
      } finally {
        setIsLoadingDecryption(false);
      }
    };

    decryptPolicyNumber();
  }, [policy_number, person?.estate_id]);

  const formatBeneficiary = (beneficiary: Beneficiary): string => {
    const parts: string[] = [];
    if (beneficiary.name) parts.push(beneficiary.name);
    if (beneficiary.relationship) parts.push(`(${beneficiary.relationship})`);
    if (beneficiary.percentage !== undefined) parts.push(`- ${beneficiary.percentage}%`);
    return parts.join(" ");
  };

  return (
    <div className="px-6 pb-6 space-y-4">
      {/* Policy Information */}
      <InsightsCard title="Policy Information" noPadding>
        <InsightsDividerList>
          {insurance_company_name && (
            <InsightsDividerRow
              label="Insurance Company"
              value={insurance_company_name}
              onClick={
                getCitationForField(citations, "insurance_company_name")
                  ? () => handleFieldClick("insurance_company_name")
                  : undefined
              }
            />
          )}
          <InsightsDividerRow
            label="Policy Number"
            value={
              <MaskedAccountNumber value={decryptedPolicyNumber} isLoading={isLoadingDecryption} />
            }
            onClick={
              getCitationForField(citations, "policy_number")
                ? () => handleFieldClick("policy_number")
                : undefined
            }
          />
          {policy_type && (
            <InsightsDividerRow
              label="Policy Type"
              value={policy_type}
              onClick={
                getCitationForField(citations, "policy_type")
                  ? () => handleFieldClick("policy_type")
                  : undefined
              }
            />
          )}
          {face_value !== undefined && (
            <InsightsDividerRow
              label="Face Value"
              value={formatCurrency(face_value)}
              onClick={
                getCitationForField(citations, "face_value")
                  ? () => handleFieldClick("face_value")
                  : undefined
              }
            />
          )}
          {cash_value !== undefined && (
            <InsightsDividerRow
              label="Cash Value"
              value={formatCurrency(cash_value)}
              onClick={
                getCitationForField(citations, "cash_value")
                  ? () => handleFieldClick("cash_value")
                  : undefined
              }
            />
          )}
          {policy_effective_date && (
            <InsightsDividerRow
              label="Effective Date"
              value={formatDate(policy_effective_date)}
              onClick={
                getCitationForField(citations, "policy_effective_date")
                  ? () => handleFieldClick("policy_effective_date")
                  : undefined
              }
            />
          )}
          {policy_expiration_date && (
            <InsightsDividerRow
              label="Expiration Date"
              value={formatDate(policy_expiration_date)}
              onClick={
                getCitationForField(citations, "policy_expiration_date")
                  ? () => handleFieldClick("policy_expiration_date")
                  : undefined
              }
            />
          )}
        </InsightsDividerList>
      </InsightsCard>

      {/* Premium Information */}
      {(premium_amount !== undefined || premium_frequency) && (
        <InsightsCard title="Premium Information" noPadding>
          <InsightsDividerList>
            {premium_amount !== undefined && (
              <InsightsDividerRow
                label="Premium Amount"
                value={formatCurrency(premium_amount)}
                onClick={
                  getCitationForField(citations, "premium_amount")
                    ? () => handleFieldClick("premium_amount")
                    : undefined
                }
              />
            )}
            {premium_frequency && (
              <InsightsDividerRow
                label="Payment Frequency"
                value={premium_frequency}
                onClick={
                  getCitationForField(citations, "premium_frequency")
                    ? () => handleFieldClick("premium_frequency")
                    : undefined
                }
              />
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {/* People */}
      {(insured_person || policy_owner) && (
        <InsightsCard title="Insured & Owner" noPadding>
          <InsightsDividerList>
            {insured_person && (
              <InsightsDividerRow
                label="Insured Person"
                value={insured_person}
                onClick={
                  getCitationForField(citations, "insured_person")
                    ? () => handleFieldClick("insured_person")
                    : undefined
                }
              />
            )}
            {policy_owner && (
              <InsightsDividerRow
                label="Policy Owner"
                value={policy_owner}
                onClick={
                  getCitationForField(citations, "policy_owner")
                    ? () => handleFieldClick("policy_owner")
                    : undefined
                }
              />
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {/* Beneficiaries */}
      {((primary_beneficiaries && primary_beneficiaries.length > 0) ||
        (contingent_beneficiaries && contingent_beneficiaries.length > 0)) && (
        <InsightsCard title="Beneficiaries" noPadding>
          <InsightsDividerList>
            {primary_beneficiaries && primary_beneficiaries.length > 0 && (
              <>
                <div className="py-2.5 px-4 bg-stone-50">
                  <span className="text-sm font-medium text-stone-700">Primary Beneficiaries</span>
                </div>
                {primary_beneficiaries.map((beneficiary, index) => (
                  <InsightsDividerRow
                    key={`primary-${index}`}
                    label={`Beneficiary ${index + 1}`}
                    value={formatBeneficiary(beneficiary)}
                    onClick={
                      getCitationForField(citations, `primary_beneficiaries.${index}.name`)
                        ? () => handleFieldClick(`primary_beneficiaries.${index}.name`)
                        : undefined
                    }
                  />
                ))}
              </>
            )}
            {contingent_beneficiaries && contingent_beneficiaries.length > 0 && (
              <>
                <div className="py-2.5 px-4 bg-stone-50">
                  <span className="text-sm font-medium text-stone-700">
                    Contingent Beneficiaries
                  </span>
                </div>
                {contingent_beneficiaries.map((beneficiary, index) => (
                  <InsightsDividerRow
                    key={`contingent-${index}`}
                    label={`Beneficiary ${index + 1}`}
                    value={formatBeneficiary(beneficiary)}
                    onClick={
                      getCitationForField(citations, `contingent_beneficiaries.${index}.name`)
                        ? () => handleFieldClick(`contingent_beneficiaries.${index}.name`)
                        : undefined
                    }
                  />
                ))}
              </>
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}
    </div>
  );
}
